import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { createLead } from "@/lib/api";
import { SERVICES, LOCALITIES } from "@/data/site";
import { ShieldCheck, Loader2 } from "lucide-react";

export default function LeadDialog({ open, onOpenChange, config }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", area: "", message: "" });

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      await createLead({ ...form, ...config.prefill, source: config.source });
      toast.success("Thank you! Our team will call you shortly.", { description: "SEAL TODAY, SAFE TOMORROW" });
      setForm({ name: "", phone: "", email: "", service: "", area: "", message: "" });
      onOpenChange(false);
    } catch (err) {
      toast.error("Something went wrong. Please try WhatsApp or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-navy-deep border-sky/20 text-white" data-testid="lead-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 text-sky mb-1">
            <ShieldCheck className="h-5 w-5" />
            <span className="text-xs font-semibold tracking-widest uppercase">JAL RAKSHAKK</span>
          </div>
          <DialogTitle className="font-display text-2xl font-extrabold">{config.title}</DialogTitle>
          <p className="text-slate-300 text-sm">Free inspection • No obligation • Response within hours</p>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-slate-200">Name*</Label>
              <Input data-testid="lead-name" value={form.name} onChange={set("name")} placeholder="Your name" className="bg-white/5 border-white/15 text-white placeholder:text-slate-500" />
            </div>
            <div className="space-y-1">
              <Label className="text-slate-200">Phone*</Label>
              <Input data-testid="lead-phone" value={form.phone} onChange={set("phone")} placeholder="+91 ..." className="bg-white/5 border-white/15 text-white placeholder:text-slate-500" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-slate-200">Email</Label>
            <Input data-testid="lead-email" value={form.email} onChange={set("email")} placeholder="you@email.com" className="bg-white/5 border-white/15 text-white placeholder:text-slate-500" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-slate-200">Service</Label>
              <Select value={form.service} onValueChange={set("service")}>
                <SelectTrigger data-testid="lead-service" className="bg-white/5 border-white/15 text-white"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {SERVICES.map((s) => <SelectItem key={s.slug} value={s.title}>{s.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-slate-200">Area</Label>
              <Select value={form.area} onValueChange={set("area")}>
                <SelectTrigger data-testid="lead-area" className="bg-white/5 border-white/15 text-white"><SelectValue placeholder="Locality" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {LOCALITIES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-slate-200">Message</Label>
            <Textarea data-testid="lead-message" value={form.message} onChange={set("message")} placeholder="Describe your leakage / requirement" className="bg-white/5 border-white/15 text-white placeholder:text-slate-500" />
          </div>
          <button data-testid="lead-submit" disabled={loading} type="submit" className="btn-ripple w-full bg-royal hover:bg-royal/90 text-white font-semibold py-3 rounded-sm transition-colors flex items-center justify-center gap-2">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {loading ? "Sending..." : "Request Free Inspection"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
